import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setUser } from '../../store/slices/userSlice';
import useAccountBreadcrumb from '../shared/useAccountBreadcrumb';
import authService from '../../services/authService';
import '../shared/ordersPageShell.css';
import './MyAccount.css';

interface ProfileFormState {
  userName: string;
  emailId: string;
  phoneNumber: string;
}

function buildFormFromUser(user: {
  userName?: string;
  firstName?: string;
  emailId?: string;
  phoneNumber?: string;
} | null): ProfileFormState {
  if (!user) {
    return { userName: '', emailId: '', phoneNumber: '' };
  }
  const displayName =
    user.userName?.trim() ||
    user.firstName?.trim() ||
    '';
  return {
    userName: displayName,
    emailId: user.emailId?.trim() || '',
    phoneNumber: user.phoneNumber?.trim() || '',
  };
}

const MyAccount: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, authReady } = useAppSelector((state) => state.user);
  const breadcrumbItems = useAccountBreadcrumb([{ label: 'My Account', active: true }]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProfileFormState>({ userName: '', emailId: '', phoneNumber: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!authReady || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authService.getCurrentUser();
        if (!cancelled && response.success && response.user) {
          dispatch(setUser(response.user));
        }
      } catch {
        // Keep existing store user if refresh fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authReady, isAuthenticated, dispatch]);

  useEffect(() => {
    setForm(buildFormFromUser(user));
  }, [user]);

  const primaryLoginMethod = user?.primaryLoginMethod;
  const emailLocked = primaryLoginMethod === 'EMAIL';
  const phoneLocked = primaryLoginMethod === 'PHONE';

  const getDisplayName = (): string => {
    if (!user) return 'User';
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (fullName) return fullName;
    const name = user.userName;
    if (name && name.trim()) return name.trim();
    if (user.emailId) {
      const local = user.emailId.split('@')[0];
      return local || user.emailId;
    }
    return 'User';
  };

  const showSignInPrompt = authReady && !isAuthenticated;

  const hasChanges = useMemo(() => {
    const baseline = buildFormFromUser(user);
    return (
      form.userName.trim() !== baseline.userName ||
      form.emailId.trim() !== baseline.emailId ||
      form.phoneNumber.trim() !== baseline.phoneNumber
    );
  }, [form, user]);

  const handleCancel = () => {
    setForm(buildFormFromUser(user));
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const response = await authService.updateProfile({
        userName: form.userName.trim(),
        emailId: form.emailId.trim(),
        phoneNumber: form.phoneNumber.trim(),
      });
      if (response.success && response.user) {
        dispatch(setUser(response.user));
        setEditing(false);
        setSuccess('Profile updated successfully.');
      } else {
        setError(response.message || 'Failed to update profile.');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update profile. Please try again.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof ProfileFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSuccess(null);
  };

  return (
    <div className="my-account-page">
      <Header />
      <PageContainer>
        <div className="orders-page-shell">
          <Breadcrumb items={breadcrumbItems} />
          <div className="account-page-header">
            <h1 className="account-page-title">My Account</h1>
          </div>
          <div className="my-account-container">
            {showSignInPrompt ? (
              <div className="my-account-card my-account-signin-prompt">
                <p>Sign in to view your account information.</p>
                <Link to="/signin" className="my-account-signin-link">
                  <i className="fa fa-sign-in" aria-hidden />
                  Sign in
                </Link>
              </div>
            ) : (
              <div className="my-account-card">
                <div className="my-account-profile">
                  <div className="my-account-avatar" aria-hidden="true">
                    <i className="fa fa-user" />
                  </div>
                  <div className="my-account-profile-text">
                    <h2 className="my-account-profile-name">{getDisplayName()}</h2>
                    {user?.emailId && (
                      <p className="my-account-profile-email">{user.emailId}</p>
                    )}
                  </div>
                  {!editing && (
                    <button
                      type="button"
                      className="my-account-edit-btn"
                      onClick={() => {
                        setEditing(true);
                        setError(null);
                        setSuccess(null);
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>

                {success && <p className="my-account-banner my-account-banner--success">{success}</p>}
                {error && <p className="my-account-banner my-account-banner--error">{error}</p>}

                {editing ? (
                  <form
                    className="my-account-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!saving && hasChanges) {
                        handleSave();
                      }
                    }}
                  >
                    <div className="my-account-field">
                      <label className="my-account-field-label" htmlFor="profile-display-name">
                        Display name
                      </label>
                      <input
                        id="profile-display-name"
                        type="text"
                        className="my-account-input"
                        value={form.userName}
                        onChange={(e) => updateField('userName', e.target.value)}
                        placeholder="Your name"
                        autoComplete="name"
                      />
                    </div>

                    <div className="my-account-field">
                      <label className="my-account-field-label" htmlFor="profile-email">
                        Email
                      </label>
                      <input
                        id="profile-email"
                        type="email"
                        className={`my-account-input${emailLocked ? ' my-account-input--locked' : ''}`}
                        value={form.emailId}
                        onChange={(e) => updateField('emailId', e.target.value.toLowerCase())}
                        placeholder="Email address"
                        autoComplete="email"
                        disabled={emailLocked}
                        readOnly={emailLocked}
                      />
                      {emailLocked && (
                        <span className="my-account-field-hint">
                          Registered with email — this cannot be changed.
                        </span>
                      )}
                    </div>

                    <div className="my-account-field">
                      <label className="my-account-field-label" htmlFor="profile-phone">
                        Phone
                      </label>
                      <input
                        id="profile-phone"
                        type="tel"
                        className={`my-account-input${phoneLocked ? ' my-account-input--locked' : ''}`}
                        value={form.phoneNumber}
                        onChange={(e) => updateField('phoneNumber', e.target.value)}
                        placeholder="Mobile number"
                        autoComplete="tel"
                        disabled={phoneLocked}
                        readOnly={phoneLocked}
                      />
                      {phoneLocked && (
                        <span className="my-account-field-hint">
                          Registered with phone — this cannot be changed.
                        </span>
                      )}
                    </div>

                    <div className="my-account-form-actions">
                      <button
                        type="button"
                        className="my-account-btn my-account-btn--secondary"
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="my-account-btn my-account-btn--primary"
                        disabled={saving || !hasChanges}
                      >
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="my-account-fields">
                    <div className="my-account-field">
                      <span className="my-account-field-label">Display name</span>
                      <span
                        className={`my-account-field-value${
                          !user?.userName && !user?.firstName ? ' my-account-field-value--empty' : ''
                        }`}
                      >
                        {user?.userName?.trim() || user?.firstName?.trim() || 'Not provided'}
                      </span>
                    </div>
                    <div className="my-account-field">
                      <span className="my-account-field-label">Email</span>
                      <span
                        className={`my-account-field-value${
                          !user?.emailId ? ' my-account-field-value--empty' : ''
                        }`}
                      >
                        {user?.emailId?.trim() || 'Not provided'}
                      </span>
                    </div>
                    <div className="my-account-field">
                      <span className="my-account-field-label">Phone</span>
                      <span
                        className={`my-account-field-value${
                          !user?.phoneNumber ? ' my-account-field-value--empty' : ''
                        }`}
                      >
                        {user?.phoneNumber?.trim() || 'Not provided'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </PageContainer>
      <Footer />
    </div>
  );
};

export default MyAccount;
